/**
 * Serving Size Adjustment Utility
 * Converts recipe measurements based on serving size changes
 */

/**
 * Adjust recipe ingredients based on serving size multiplier
 * @param {Array} ingredients - Original ingredients array
 * @param {number} originalServings - Original number of servings
 * @param {number} newServings - Desired number of servings
 * @returns {Array} - Adjusted ingredients with converted measurements
 */
export function adjustRecipeServings(ingredients, originalServings, newServings) {
  const multiplier = newServings / originalServings;
  
  return ingredients.map(ingredient => {
    const adjusted = adjustIngredient(ingredient, multiplier);
    return {
      ...adjusted,
      original: ingredient,
      multiplier: multiplier.toFixed(2)
    };
  });
}

/**
 * Adjust a single ingredient's measurements
 * @param {string} ingredient - Original ingredient string
 * @param {number} multiplier - Conversion multiplier
 * @returns {Object} - Adjusted ingredient with converted measurements
 */
function adjustIngredient(ingredient, multiplier) {
  // Common measurement patterns
  const measurements = [
    // Volume measurements
    { pattern: /(\d+(?:\.\d+)?)\s*(cups?|cup)\b/i, type: 'volume', baseUnit: 'cups' },
    { pattern: /(\d+(?:\.\d+)?)\s*(tbsp|tablespoons?|tablespoon)\b/i, type: 'volume', baseUnit: 'tbsp' },
    { pattern: /(\d+(?:\.\d+)?)\s*(tsp|teaspoons?|teaspoon)\b/i, type: 'volume', baseUnit: 'tsp' },
    { pattern: /(\d+(?:\.\d+)?)\s*(fl\s*oz|fluid\s*ounces?)\b/i, type: 'volume', baseUnit: 'fl oz' },
    { pattern: /(\d+(?:\.\d+)?)\s*(ml|milliliters?)\b/i, type: 'volume', baseUnit: 'ml' },
    { pattern: /(\d+(?:\.\d+)?)\s*(liters?|l)\b/i, type: 'volume', baseUnit: 'liters' },
    
    // Weight measurements
    { pattern: /(\d+(?:\.\d+)?)\s*(lbs?|pounds?)\b/i, type: 'weight', baseUnit: 'pounds' },
    { pattern: /(\d+(?:\.\d+)?)\s*(oz|ounces?)\b/i, type: 'weight', baseUnit: 'ounces' },
    { pattern: /(\d+(?:\.\d+)?)\s*(g|grams?)\b/i, type: 'weight', baseUnit: 'grams' },
    { pattern: /(\d+(?:\.\d+)?)\s*(kg|kilograms?)\b/i, type: 'weight', baseUnit: 'kg' },
    
    // Count measurements
    { pattern: /(\d+(?:\.\d+)?)\s*(large|medium|small)\s+(eggs?|egg)\b/i, type: 'count', baseUnit: 'eggs' },
    { pattern: /(\d+(?:\.\d+)?)\s*(cloves?|clove)\s+(of\s+)?(garlic|garlic)\b/i, type: 'count', baseUnit: 'cloves' },
    { pattern: /(\d+(?:\.\d+)?)\s*(slices?|slice)\b/i, type: 'count', baseUnit: 'slices' },
    
    // Simple numbers (no unit specified)
    { pattern: /^(\d+(?:\.\d+)?)\s+(?!cups?|tbsp|tsp|oz|lbs?|g|kg|ml|l|large|medium|small|cloves?|slices?)/i, type: 'number', baseUnit: 'units' }
  ];
  
  let adjustedIngredient = ingredient;
  let hasMeasurement = false;
  
  for (const measurement of measurements) {
    const match = ingredient.match(measurement.pattern);
    if (match) {
      const originalAmount = parseFloat(match[1]);
      const unit = match[2] || match[3] || match[4] || 'units';
      const newAmount = originalAmount * multiplier;
      
      // Format the new amount nicely
      const formattedAmount = formatAmount(newAmount);
      
      // Replace in the ingredient string
      adjustedIngredient = ingredient.replace(
        measurement.pattern,
        `${formattedAmount} ${unit}`
      );
      
      hasMeasurement = true;
      break;
    }
  }
  
  // If no measurement found, try to find simple fractions or whole numbers
  if (!hasMeasurement) {
    const simpleNumberMatch = ingredient.match(/^(\d+(?:\/\d+)?)\s+/);
    if (simpleNumberMatch) {
      const originalAmount = parseFraction(simpleNumberMatch[1]);
      const newAmount = originalAmount * multiplier;
      const formattedAmount = formatAmount(newAmount);
      
      adjustedIngredient = ingredient.replace(
        /^(\d+(?:\/\d+)?)\s+/,
        `${formattedAmount} `
      );
    }
  }
  
  return {
    ingredient: adjustedIngredient,
    hasConversion: hasMeasurement,
    originalServings: 1, // Will be set by caller
    newServings: multiplier
  };
}

/**
 * Format amount to readable format with proper mixed fractions
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted amount
 */
function formatAmount(amount) {
  // Handle whole numbers
  if (Math.abs(amount - Math.round(amount)) < 0.01) {
    return Math.round(amount).toString();
  }
  
  // Handle fractions less than 1
  if (amount < 1) {
    const fraction = decimalToFraction(amount);
    if (fraction) return fraction;
    // Fallback to decimal with 2 places
    return amount.toFixed(2);
  }
  
  // Handle mixed numbers (e.g., 2 1/3)
  const wholeNumber = Math.floor(amount);
  const decimal = amount - wholeNumber;
  
  if (decimal > 0.01) {
    const fractionPart = decimalToFraction(decimal);
    if (fractionPart) {
      return `${wholeNumber} ${fractionPart}`;
    }
  }
  
  // Round to reasonable precision
  if (amount < 10) {
    return Math.round(amount * 100) / 100; // 2 decimal places
  } else {
    return Math.round(amount * 10) / 10; // 1 decimal place
  }
}

/**
 * Convert decimal to fraction
 * @param {number} decimal - Decimal number
 * @returns {string|null} - Fraction string or null
 */
function decimalToFraction(decimal) {
  const commonFractions = [
    { decimal: 0.125, fraction: '1/8' },
    { decimal: 0.1667, fraction: '1/6' },
    { decimal: 0.25, fraction: '1/4' },
    { decimal: 0.3333, fraction: '1/3' },
    { decimal: 0.375, fraction: '3/8' },
    { decimal: 0.5, fraction: '1/2' },
    { decimal: 0.625, fraction: '5/8' },
    { decimal: 0.6667, fraction: '2/3' },
    { decimal: 0.75, fraction: '3/4' },
    { decimal: 0.8333, fraction: '5/6' },
    { decimal: 0.875, fraction: '7/8' }
  ];
  
  // Use tighter tolerance for better accuracy
  for (const frac of commonFractions) {
    if (Math.abs(decimal - frac.decimal) < 0.02) {
      return frac.fraction;
    }
  }
  
  return null;
}

/**
 * Parse fraction string to decimal
 * @param {string} fraction - Fraction string like "1/2" or "3/4"
 * @returns {number} - Decimal value
 */
function parseFraction(fraction) {
  if (fraction.includes('/')) {
    const [numerator, denominator] = fraction.split('/');
    return parseFloat(numerator) / parseFloat(denominator);
  }
  return parseFloat(fraction);
}

/**
 * Get conversion suggestions for common measurements
 * @param {number} multiplier - Conversion multiplier
 * @returns {Array} - Array of conversion suggestions
 */
export function getConversionSuggestions(multiplier) {
  const suggestions = [];
  
  if (multiplier > 1) {
    suggestions.push({
      type: 'increase',
      message: `Recipe will be ${multiplier.toFixed(1)}x larger`,
      tips: [
        'Consider using a larger baking dish',
        'May need to adjust cooking time',
        'Check if your oven can handle the larger batch'
      ]
    });
  } else if (multiplier < 1) {
    suggestions.push({
      type: 'decrease', 
      message: `Recipe will be ${(1/multiplier).toFixed(1)}x smaller`,
      tips: [
        'Use a smaller baking dish',
        'Reduce cooking time by 10-15%',
        'Watch carefully as smaller batches cook faster'
      ]
    });
  }
  
  // Add specific measurement tips
  if (multiplier >= 2) {
    suggestions.push({
      type: 'large_batch',
      message: 'Large batch cooking tips:',
      tips: [
        'Mix in smaller batches to ensure even mixing',
        'May need to cook in multiple pans',
        'Consider dividing into smaller portions for storage'
      ]
    });
  }
  
  return suggestions;
}

/**
 * Adjust cooking times based on serving size
 * @param {string} originalTime - Original cooking time
 * @param {number} multiplier - Serving size multiplier
 * @returns {string} - Adjusted cooking time with explanation
 */
export function adjustCookingTime(originalTime, multiplier) {
  const timeMatch = originalTime.match(/(\d+)\s*(minutes?|mins?|hours?|hrs?)/i);
  if (!timeMatch) return originalTime;
  
  const originalMinutes = parseInt(timeMatch[1]);
  const unit = timeMatch[2].toLowerCase();
  
  let adjustedMinutes;
  let explanation = '';
  
  if (unit.includes('hour')) {
    adjustedMinutes = originalMinutes * 60;
  } else {
    adjustedMinutes = originalMinutes;
  }
  
  // Adjust time based on multiplier
  if (multiplier >= 2) {
    // Large batches need slightly more time
    adjustedMinutes = Math.round(adjustedMinutes * (1 + (multiplier - 1) * 0.1));
    explanation = `Large batch: +${Math.round((multiplier - 1) * 10)}% time`;
  } else if (multiplier <= 0.5) {
    // Small batches cook faster
    adjustedMinutes = Math.round(adjustedMinutes * 0.85);
    explanation = `Small batch: -15% time`;
  } else {
    explanation = 'Time adjustment: minimal';
  }
  
  // Convert back to original unit
  let displayTime;
  if (unit.includes('hour') && adjustedMinutes >= 60) {
    displayTime = `${Math.round(adjustedMinutes / 60)} hours`;
  } else {
    displayTime = `${adjustedMinutes} minutes`;
  }
  
  return `${displayTime} (${explanation})`;
}








